import { useCallback, useEffect, useMemo, useState } from 'react'
import { NextRouter } from 'next/router'
import { ParsedUrlQuery } from 'querystring'

import { LoanSelectorStateType } from '../components/banklist/loan-selector/typings'
import { BankListItem, SetState } from '../components/banklist/typings'

import {
  BANKLIST_DEFAULT_SELECTION_VALUES,
  PURPOSE,
  adjustLoanAmount,
} from '../utils/loanSelector.utils'
import {
  fetchBankList,
  getPrefillResponse,
  getPrefillResumeHashFromStorage,
  setPrefillResumeHashToStorage,
} from '../utils/banklist'
import {
  getAdvertisementId,
  getRefId,
  mapQueryParamsToTeal,
} from '../utils/url'
import { EntryPoint } from '../utils/entryPoint.enum'
import { logAndTrackError } from '../utils/error'

interface UseBankListProps {
  pathname?: string
  regRoutePath: string
  queryParams: ParsedUrlQuery
  router: NextRouter
}

export const LOADING_FETCH_STATUS_CODE = -999

export const useBankList = ({
  pathname = '/banklist',
  regRoutePath,
  queryParams,
  router,
}: UseBankListProps) => {
  const advertisementId = getAdvertisementId(queryParams)
  const refId = getRefId(queryParams)
  const {
    subId,
    purpose = BANKLIST_DEFAULT_SELECTION_VALUES.purpose,
    amount = BANKLIST_DEFAULT_SELECTION_VALUES.amount,
    term = BANKLIST_DEFAULT_SELECTION_VALUES.term,
    palette,
    hasDac,
  } = mapQueryParamsToTeal(queryParams)

  const [loanSelectorState, setLoanSelectorState] = useState({
    purpose,
    amount,
    term,
  })

  useEffect(() => {
    setLoanSelectorState({
      purpose,
      amount,
      term,
    })
  }, [amount, purpose, term])

  const [currentBankList, setCurrentBankList] = useState<
    BankListItem[] | undefined
  >([])

  const [fetchStatus, setFetchStatus] = useState<number>(
    LOADING_FETCH_STATUS_CODE,
  )

  useEffect(() => {
    ;(async function fetcher() {
      try {
        setCurrentBankList([])
        setFetchStatus(LOADING_FETCH_STATUS_CODE)
        const bankListResponse = await fetchBankList(
          process.env.NEXT_PUBLIC_PAAPI_BANKLIST_URL as string,
          purpose as string,
          parseInt(amount as string, 10),
          parseInt(term as string, 10),
          advertisementId,
        )
        if (!bankListResponse?.errors && bankListResponse?.result?.banklist) {
          setCurrentBankList(bankListResponse?.result?.banklist)
        }
        if (bankListResponse?.errors) {
          logAndTrackError(new Error(bankListResponse.errors), 'error')

          router.push({
            pathname: regRoutePath,
            query: {
              entryPoint: EntryPoint.BaseReceived,
              ...router.query,
            },
          })
        }
        setFetchStatus(bankListResponse?.status)
      } catch (err: any) {
        logAndTrackError(new Error(err.toString()), 'error')

        router.push({
          pathname: regRoutePath,
          query: {
            entryPoint: EntryPoint.BaseReceived,
            ...router.query,
          },
        })
      }
    })()
  }, [advertisementId, amount, purpose, term, router])

  const handleLoanSelectorValueChange = useCallback(
    (lsState: LoanSelectorStateType) => {
      if (lsState) {
        router?.push({
          pathname,
          query: {
            ...queryParams,
            purpose: lsState?.purpose as string,
            amount: `${lsState?.amount}`,
            ...(lsState?.purpose === PURPOSE.CAR && {
              vehiclePrice: lsState?.amount,
            }),
            term: `${lsState?.term}`,
            advertisementId,
            refId,
            ...(subId && { subId }),
          },
        })
      }
    },
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [],
  )

  const handleCTAButtonClick = useCallback(
    async (
      setIsDisabled: SetState,
      productId: string,
      currentLoanSelectorState: LoanSelectorStateType,
    ) => {
      const resumeHash = getPrefillResumeHashFromStorage()

      const payload = {
        purpose: currentLoanSelectorState.purpose,
        amount: currentLoanSelectorState.amount,
        term: currentLoanSelectorState.term,
        termsAccepted: false,
        dataProcessingConsentGiven: false,
        advertisementId,
        ...(resumeHash && { resumeHash }),
        productId: parseInt(productId, 10),
        refId,
        subId,
      }

      try {
        const prefillResponse = await getPrefillResponse(payload)
        if (prefillResponse?.errors?.length === 0) {
          const resume = prefillResponse.result.url.match(/resume=([^&]*)/)?.[1]
          if (resume) {
            // Store resumeHash in localStorage
            setPrefillResumeHashToStorage(
              prefillResponse.result?.resumeHash || '',
            )

            router.push({
              pathname: regRoutePath,
              query: {
                entryPoint: EntryPoint.BaseReceived,
                resume,
                palette,
                backUrl: document.location.href,
                ...(hasDac && { hasDac }),
              },
            })
          }
        }
      } finally {
        setIsDisabled({
          [productId]: false,
        })
      }
    },
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [],
  )

  const bankListContextData = useMemo(
    () => ({
      loanSelectorState: {
        purpose: loanSelectorState?.purpose as string,
        amount: adjustLoanAmount(loanSelectorState?.amount),
        term: parseInt(loanSelectorState?.term as string, 10),
      },
      handleCTAButtonClick,
      handleLoanSelectorValueChange,
    }),
    [
      handleCTAButtonClick,
      handleLoanSelectorValueChange,
      loanSelectorState?.amount,
      loanSelectorState?.purpose,
      loanSelectorState?.term,
    ],
  )

  return {
    bankListContextData,
    currentBankList,
    fetchStatus,
    advertisementId,
    refId,
  }
}
